import { useContext, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import {
  MessageGenerationTable,
  MessageGenerationSessionHeader,
} from '../../components/MessageGeneration';
import {
  MessageGenerationCreateSessionContext,
  MessageGenerationLoadSessionContext,
  MessageGenerationStateContext,
  MessageGenerationDispatchContext,
} from '../MessageGenSession/Context';

type Props = {
  segmentationSessionId: string;
  segmentId: string;
  // segmentName: string;
};

export const SegmentMessageGeneration = (props: Props) => {
  const session = useContext(MessageGenerationStateContext);
  const dispatch = useContext(MessageGenerationDispatchContext);
  const createMessageGenSession = useContext(MessageGenerationCreateSessionContext);
  const loadSession = useContext(MessageGenerationLoadSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const apiClient = useChatApiClient();

  const hasExistingSession = !!session;

  useEffect(() => {
    if (!hasExistingSession) {
      console.log('No existing message generation session found, creating a new one...');
      if (!props.segmentationSessionId) {
        console.error('segmentationSessionId is required to create message generation session');
        return;
      }
      if (!brandIdentifier) {
        console.error('brandIdentifier is required to create message generation session');
        return;
      }
      if (!props.segmentId) {
        console.error('segmentId is required to create message generation session');
        return;
      }
      console.log(
        'Creating message generation session with segmentationSessionId:',
        props.segmentationSessionId,
        'segmentId:',
        props.segmentId
      );
      createMessageGenSession({
        sessionId: props.segmentationSessionId,
        brandIdentifier,
        initialState: {
          segmentIds: [props.segmentId],
        },
      });
    }
  }, [
    hasExistingSession,
    createMessageGenSession,
    props.segmentationSessionId,
    props.segmentId,
    brandIdentifier,
  ]);

  const messages = useMemo(() => {
    if (!session) return [];
    return session.state.messages;
  }, [session]);

  const isGenerating = session?.state.phase === 'generating';
  const numberOfMessages = session?.state.numberOfMessages || 5;
  const phase = session?.state.phase || 'idle';
  const isSegment = session?.state.segmentIds && session.state.segmentIds.length === 1;
  const type = isSegment ? 'segment' : 'run';
  const brandName = session?.state.brandName;
  const objective = session?.state.objective;

  // Fetch saved identifiers when session loads
  const { data: savedIdentifiers } = useQuery({
    queryKey: ['saved-message-identifiers', session?.id],
    queryFn: () => {
      if (!session?.id) throw new Error('No session ID');
      return apiClient.messageGeneration.getSavedMessageIdentifiers(session.id);
    },
    enabled: !!session?.id,
    staleTime: 30000,
  });

  // Merge saved status into messages
  const messagesWithSavedStatus = useMemo(() => {
    if (!savedIdentifiers || savedIdentifiers.length === 0) return messages;
    return messages.map((msg) => ({
      ...msg,
      saved: savedIdentifiers.includes(msg.identifier),
    }));
  }, [messages, savedIdentifiers]);

  const saveMutation = useMutation({
    mutationFn: async (messageIdentifiers: string[]) => {
      if (!session?.id) throw new Error('No session ID available');
      return apiClient.messageGeneration.saveMessages(session.id, messageIdentifiers);
    },
    onSuccess: (data) => {
      if (session && dispatch && data.savedIdentifiers.length > 0) {
        const updatedMessages = session.state.messages.map((msg) =>
          data.savedIdentifiers.includes(msg.identifier) ? { ...msg, saved: true } : msg
        );
        dispatch({ ...session.state, messages: updatedMessages });
      }
    },
  });

  const handleSelectSession = (sessionId: number) => {
    if (loadSession) {
      loadSession(sessionId);
    }
  };

  const handleResetSession = () => {
    if (!createMessageGenSession || !brandIdentifier) return;

    // Generate a new session ID (UUID)
    const newSessionId = crypto.randomUUID();

    // Create new session with current segment (this will create a new backend session with fresh chat instance)
    createMessageGenSession({
      sessionId: newSessionId,
      brandIdentifier,
      initialState: {
        segmentIds: [props.segmentId], // Keep the current segment ID
      },
    });
  };

  if (!session) {
    return null;
  }

  return (
    <Container>
      <MessageGenerationSessionHeader
        sessionId={session.id}
        sessionName=""
        createdOn={session.createdOn}
        phase={phase}
        type={type}
        brandName={brandName}
        objective={objective}
        showPageTitle={true}
        showSessionHistory={true}
        sessionHistoryIconOnly={true}
        onSelectSession={handleSelectSession}
      />
      <MessageGenerationTable
        messages={messagesWithSavedStatus}
        isGenerating={isGenerating}
        sessionId={session.id}
        numberOfMessages={numberOfMessages}
        onSaveSelected={(identifiers) => saveMutation.mutate(identifiers)}
        onResetSession={handleResetSession}
        isSaving={saveMutation.isPending}
        phase={phase}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0;
`;
